import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { RefreshCw, Upload, Save } from 'lucide-react';
import NodeV2LeftPanel from '../components/NodeV2LeftPanel';
import NodeV2BlockList from '../components/NodeV2BlockList';
import { FEATHER_MAP } from '../components/featherIcons';
import TimelineNode from '../components/TimelineNode';
import NodeV2ContextMenu from '../components/NodeV2ContextMenu';
import NodeOptionsPanel from '../components/NodeOptionsPanel';
import Modal from '../components/Modal';
import UserSettingsModal from '../components/UserSettingsModal';
import MagicWordSettingsModal from '../components/MagicWordSettingsModal';
import FeedbackPanel from '../components/FeedbackPanel';
import HelpDocPopup from '../components/HelpDocPopup';
import BlockEditorPanel from '../components/BlockEditorPanel';
import ConfirmPopup from '../components/ConfirmPopup';
import NamePromptModal from '../components/NamePromptModal';
import ConflictInbox from '../components/ConflictInbox';
import ConflictResolveView from '../components/ConflictResolveView';
import { parseBlocks, serializeBlocks, computeUnsyncedIndices } from '../services/block-utils';
import { diff3Blocks } from '../services/diff3';
import { fileGetContent, getUserSettings, getActiveNode, setActiveNode, saveUserBlockSettings, redirectToLogin, fileConflicts, fileStatus, fileConflictDetail } from '../services/api';
import { saveWithRetry, buildTimeMarkLine, applyTimeMark } from '../services/save-utils';
import { outboxGetForRead, outboxList, outboxGet, outboxRemove, outboxPut, subscribe, runSync, resetSyncBackoff, saveFileLocal, draftGet, draftClear, recoveryFlagGet, recoveryFlagClear, applyFileStatuses } from '../services/outbox';
import useNodeSettings from '../hooks/useNodeSettings';

/**
 * Return the folder path that TimelineNode should scan for date-based
 * .md files given the selected node.
 */
function resolveTimelineFolderPath(selectedNode) {
  if (!selectedNode) return null;
  if (selectedNode.type === 'timeline') {
    return selectedNode.path;
  }
  return null;
}

/** Return today's date string in YYYY-MM-DD format (local time). */
function localTodayStr() {
  return new Date().toLocaleDateString('sv-SE');
}

/** Return the parent directory path of a node path ('' for top level). */
function parentPath(path) {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
}

/** Format a timestamp (ms) as "HH:mm". */
function formatUpdatedAt(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Render a node icon — Feather SVG ("feather:name") or emoji, same as the tree. */
function renderNodeIcon(icon, className = 'v2-node-icon') {
  if (icon && icon.startsWith('feather:')) {
    const IconComponent = FEATHER_MAP[icon.slice(8)];
    if (IconComponent) {
      return (
        <span className={className}>
          <IconComponent size={14} strokeWidth={2} />
        </span>
      );
    }
  }
  return <span className={className}>{icon}</span>;
}

/**
 * How often the foreground status poll checks in-flight (syncing) saves.
 *
 * The backend sync worker only pushes a dirty entry after its own debounce
 * (CACHE_SYNC_DEBOUNCE_SECS, default 10 s).  A /file/status answer therefore
 * cannot change within that window, so polling faster than the debounce is
 * pure waste.  Poll at the same cadence and accept up to ~10 s of extra
 * resolution latency.
 */
const SYNC_STATUS_POLL_MS = 10_000;

/**
 * Minimum time the "Saving…" (upload — local → server) phase stays visible
 * in the header before it may advance to "Syncing…" (save — server → Git).
 * The backend ACKs POST /file/save after a cache write (the Git push is
 * deferred to the sync worker), so without this floor the upload phase
 * flashes by in ~100 ms and is effectively invisible.
 */
const MIN_SAVING_DISPLAY_MS = 600;

/**
 * Minimum time the "Refreshing…" indicator stays visible during a
 * pull-to-refresh.  The refresh read resolves in ~100 ms on a warm cache,
 * so without this floor the indicator flashes by and is never seen.
 */
const MIN_REFRESH_DISPLAY_MS = 600;

/**
 * Node/Block Editor — left-right split layout.
 *
 * Left panel: quick access + file tree (collapsible to 48px icon strip).
 * Right panel: block list with move icons, edit via modal, save via batch.
 */
function Node() {
  const {
    toast,
    openDataSourceSetup,
  } = useOutletContext();

  // ── Layout state ───────────────────────────────────────────
  const PANEL_MIN = 64;
  const PANEL_MAX = 320;
  const COMMIT_RATIO = 0.35;
  const [leftWidth, setLeftWidth] = useState(PANEL_MIN); // 64 = collapsed, 320 = expanded
  const [dragCommit, setDragCommit] = useState(false); // true when drag crossed threshold
  const [screenSwipe, setScreenSwipe] = useState(null); // { ratio, committed } | null — full-screen right-edge indicator
  const [selectedNode, setSelectedNode] = useState(null); // { path, type }
  const [treeFocusPath, setTreeFocusPath] = useState(null); // transient highlight in the tree (search reveal)
  const [nodeNotFound, setNodeNotFound] = useState(false); // true when selected file doesn't exist
  const [nodeOffline, setNodeOffline] = useState(false); // true when a non-inbox read failed (offline)
  const [inboxOffline, setInboxOffline] = useState(false); // inbox read failed — allow additive local-only editing
  const [dataSourceReady, setDataSourceReady] = useState(false); // true once settings are loaded & configured

  // ── Tree data ─────────────────────────────────────────────
  const [treeData, setTreeData] = useState([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeExpanded, setTreeExpanded] = useState({}); // folder -> children[]
  const [focusedFolder, setFocusedFolder] = useState(''); // deepest expanded folder (add-node target)

  // ── Block state ───────────────────────────────────────────
  const [blocks, setBlocks] = useState([]);
  const [fileSha, setFileSha] = useState(null);
  const [baseContent, setBaseContent] = useState(null); // last-synced content (badge diff base)
  const [conflictInfo, setConflictInfo] = useState(null); // { remoteContent, remoteSha } when the viewed file is in conflict
  const [syncState, setSyncState] = useState(null); // 'dirty' | 'syncing' | null — viewed file's in-flight sync state
  const savingStampRef = useRef(null); // { path, at } | null — when the visible "Saving…" phase began
  const savingTimerRef = useRef(null); // one-shot timer to re-check after the min display window
  const [savingPaths, setSavingPaths] = useState(new Set()); // file paths actively uploading (dirty) or saving (syncing)
  const [conflicts, setConflicts] = useState([]); // [{ path, type: 'device'|'external' }]
  // Per-node conflict paths (device + external) for the sidebar warning dot.
  const conflictPaths = useMemo(() => new Set((conflicts || []).map((c) => c.path)), [conflicts]);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [conflictView, setConflictView] = useState(null); // null | 'inbox' | 'resolve'
  const [resolvingConflict, setResolvingConflict] = useState(null); // { path, type } | null
  const [blockLoading, setBlockLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const refreshTimerRef = useRef(null); // one-shot timer that hides "Refreshing…" after the min display window
  const rawContentRef = useRef(null);   // latest local content of the viewed file
  const baseUnknownRef = useRef(false); // true when the viewed file's remote base is unknown (offline read)
  const lastPulledAtRef = useRef(0);    // timestamp of the last successful read
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null); // shown next to the node name

  // ── Modal state ───────────────────────────────────────────
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [quickTextSettingsOpen, setQuickTextSettingsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalInitial, setModalInitial] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // ── Block auto-save preferences ─────────────────────────────
  const [blockAutoSaveWord, setBlockAutoSaveWord] = useState('over');
  const [blockAutoSaveDelay, setBlockAutoSaveDelay] = useState(3);

  // ── Reorder toggle (mobile) ────────────────────────────────
  // Default OFF — touch events pass through for native scroll.
  // Toggle ON to enable vertical drag-to-reorder of blocks.
  // Horizontal swipe (move/options) always works regardless of toggle.
  const [swipeEnabled, setSwipeEnabled] = useState(false);

  // ── Move mode state ───────────────────────────────────────
  const [moveMode, setMoveMode] = useState(false);
  const [moveSource, setMoveSource] = useState(null); // { filePath, blockIndex, blockContent }

  // ── Block delete confirmation (right-swipe) ────────────────
  const [confirmBlockDelete, setConfirmBlockDelete] = useState(null); // { filePath, blockIndex } | null

  // ── Context menu state ────────────────────────────────────
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, targetPath: '' });

  // ── Name prompt (first-run, shown when name is empty) ────
  const [namePrompt, setNamePrompt] = useState({ show: false, defaultName: '', sourceHint: '' });

  // ── New node filename prompt ──────────────────────────────
  const [filenamePrompt, setFilenamePrompt] = useState({ show: false, targetPath: '' });
  const [newFilename, setNewFilename] = useState('');
  const [newNodeOp, setNewNodeOp] = useState('move'); // 'move' | 'copy'

  // ── Unified node/folder settings panel state ──
  const [nodeOptions, setNodeOptions] = useState(null); // { path, name, type } | null

  // ── Global "show all" toggle — reveal hidden folders in the tree ──
  const [showAllFolders, setShowAllFolders] = useState(false);

  // ── Confirm delete state ──────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(null); // { path, name } | null

  // ── Add node popup state ─────────────────────────────────
  const [addNodePopup, setAddNodePopup] = useState({ show: false, targetPath: '' });
  const [addNodeName, setAddNodeName] = useState('');
  const [addNodeType, setAddNodeType] = useState('file'); // 'file' | 'folder'
  const [addNodeSubmitting, setAddNodeSubmitting] = useState(false);
  const [addNodeError, setAddNodeError] = useState('');

  const prevExpandedRef = useRef(true);
  const initialLoadDoneRef = useRef(false);
  const focusedFolderRestoredRef = useRef(false);
  const activeNodePromise = useRef(null);
  const [timelineCurrentPath, setTimelineCurrentPath] = useState('');

  // ── Derived paths ─────────────────────────────────────────
  // Everything tracks from the repository root — there is no base path.
  const notesRoot = '';
  const inboxPath = 'inbox.md';
  const isInboxPath = useCallback((path) => !!path && path === inboxPath, [inboxPath]);

  // ── Node settings (icon + extras per node) ──────────────
  const {
    icons: nodeIcons,
    defaults: nodeDefaults,
    settings: allNodeSettings,
    quickAccessNodes,
    hiddenPaths,
    tree,
    loading: nodeSettingsLoading,
    setNodeSetting,
    getNodeSetting,
    refresh: refreshNodeSettings,
    createNode,
    createFolder,
    deleteNode,
    renameNode,
    fetchChildren,
  } = useNodeSettings();

  // ── Derived view state ────────────────────────────────────
  const isTimelineView = selectedNode?.type === 'timeline';

  // Track which folders are currently being lazy-loaded
  const [loadingFolders, setLoadingFolders] = useState(new Set());

  // ── Load user settings ──────────────────────────────────
  useEffect(() => {
    getUserSettings()
      .then(s => {
        const ds = s.data_source;
        // Auto-open onboarding wizard if not configured
        if (!ds?.provider || !ds?.repo_name) {
          openDataSourceSetup('onboarding');
          return;
        }
        // Provider + repo are set but there's no personal access token.
        // File access is PAT-only, so route the user to the settings sheet
        // to add one instead of landing on a page that can't load anything.
        if (!ds?.has_token) {
          openDataSourceSetup('settings');
          return;
        }
        setDataSourceReady(true);

        // Load block auto-save preferences
        const ss = s.save_settings || {};
        setBlockAutoSaveWord(ss.block_auto_save_word || 'over');
        setBlockAutoSaveDelay(ss.block_auto_save_delay ?? 3);

        // Show name prompt when display name is empty
        if (!s.name) {
          const provider = ds.provider || '';
          const hint = provider
            ? `From your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account`
            : '';
          setNamePrompt({ show: true, defaultName: s.email || '', sourceHint: hint });
        }
      })
      .catch(() => { redirectToLogin(); });
  }, []);

  // ── Recover a draft only when the session actually expired ──
  // The editor writes its draft on every keystroke, so a draft can exist for a
  // normal, already-finished edit.  Only recover when the recovery flag was set
  // at session expiry (api.js redirectToLogin).  The flag is consumed here so
  // recovery is a one-shot offer per expiry; a stale leftover draft without the
  // flag is ignored and never pops on a plain app open.
  const draftReadyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const flag = await recoveryFlagGet();
      if (cancelled || !flag) return;
      const draft = await draftGet();
      if (cancelled) return;
      if (!draft) {
        recoveryFlagClear();
        return;
      }
      const ageHours = (Date.now() - (draft.timestamp || 0)) / (1000 * 60 * 60);
      if (ageHours >= 24) {
        draftClear();
        recoveryFlagClear();
        return;
      }
      recoveryFlagClear(); // consume the flag — one recovery offer per expiry
      draftReadyRef.current = true; // signal to auto-open modal once file is loaded
      toast('warning', 'Unsaved content has been recovered from your last session.', 8000);
      // Navigate to the file where the draft was being edited
      if (draft.filePath && draft.filePath !== selectedNode?.path) {
        selectNode(draft.filePath);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-open the block modal with the recovered draft once a file is loaded.
  // Uses the draft's block index + content fingerprint to decide in-place edit.
  // If the original block changed, the edit is treated as already saved and the
  // draft is discarded (no duplicate block).
  useEffect(() => {
    if (!draftReadyRef.current || !selectedNode || blockLoading) return;
    draftReadyRef.current = false;
    draftGet().then((draft) => {
      if (!draft) return;
      const { blockIndex, originalContent, newContent } = draft;
      if (blockIndex !== null && blockIndex !== undefined && selectedNode.type === 'file') {
        if (blocks[blockIndex] !== originalContent) {
          draftClear();
          return;
        }
        setEditingIndex(blockIndex);
      } else {
        setEditingIndex(null);
      }
      setModalInitial(newContent || '');
      setModalOpen(true);
    });
  }, [selectedNode, blockLoading, blocks]);

  // ── Derive root tree from node map ─────────────────────────
  // The tree is pre-built by the backend — no async fetch needed.
  const loadTree = useCallback(() => {
    if (nodeSettingsLoading) return;  // wait for initial fetch to complete
    const children = tree[notesRoot];
    if (children) {
      setTreeData(children);
    }
    setTreeLoading(false);
  }, [tree, notesRoot, nodeSettingsLoading]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // Keep expanded folders in sync with the authoritative tree map — an
  // optimistic delete changes a subfolder's children without a re-expand,
  // and the treeExpanded snapshots would otherwise go stale.
  useEffect(() => {
    setTreeExpanded(prev => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const k of keys) {
        if (tree[k] && next[k] !== tree[k]) {
          next[k] = tree[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tree]);

  // ── Persist the focused (deepest expanded) folder ──────────
  // Stored locally so the tree branch + add-node target survive a reload.
  useEffect(() => {
    if (focusedFolder) {
      localStorage.setItem('julynote_focused_folder', focusedFolder);
    } else {
      localStorage.removeItem('julynote_focused_folder');
    }
  }, [focusedFolder]);

  // ── Flat node list for send-to-node selector ──────────────
  const flatNodeList = useMemo(() => {
    const collectFiles = (items) =>
      items.filter(item => item.type === 'blob' && item.path && !isInboxPath(item.path));

    const rootFiles = collectFiles(treeData);
    const expandedFiles = Object.values(treeExpanded).flatMap(children => collectFiles(children || []));
    // Merge and deduplicate by path
    const seen = new Set();
    return [...rootFiles, ...expandedFiles].filter(item => {
      if (seen.has(item.path)) return false;
      seen.add(item.path);
      return true;
    }).sort((a, b) => (a.path || '').localeCompare(b.path || ''));
  }, [treeData, treeExpanded, isInboxPath]);

  // ── Destinations for block edit modal ───────────────────────
  const blockModalDestinations = useMemo(() => {
    const dests = [
      { type: 'nodes', label: 'Notes', icon: '' },
    ];
    for (const qa of (quickAccessNodes || [])) {
      if (qa.type === 'folder') continue; // folders aren't valid block destinations
      if (qa.path === selectedNode?.path) continue; // skip self
      dests.push({
        type: 'node',
        path: qa.path,
        label: qa.name.replace(/\.md$/, ''),
        icon: qa.icon || '',
      });
    }
    return dests;
  }, [quickAccessNodes, selectedNode?.path]);

  // ── Toggle folder expand (lazy-loads children on first expand) ─
  // Single-path accordion: only one folder branch stays expanded at a time.
  const toggleFolder = async (folderPath) => {
    // Collapse branch: remove this folder and every descendant (their subtrees
    // stop rendering), and move focus up to the nearest still-expanded ancestor.
    if (treeExpanded[folderPath]) {
      setTreeExpanded(prev => {
        const next = {};
        for (const key of Object.keys(prev)) {
          if (key === folderPath || key.startsWith(folderPath + '/')) continue;
          next[key] = prev[key];
        }
        return next;
      });
      setFocusedFolder(() => {
        let p = parentPath(folderPath);
        while (p && !treeExpanded[p]) p = parentPath(p);
        return p;
      });
      return;
    }

    // Expand: keep only this folder's ancestor prefixes expanded (collapsing
    // sibling branches), then expand the folder itself.
    setTreeExpanded(prev => {
      const next = {};
      for (const key of Object.keys(prev)) {
        if (folderPath.startsWith(key + '/')) next[key] = prev[key];
      }
      return next;
    });

    // Use cached children if available, otherwise lazy-load
    if (tree[folderPath]) {
      setTreeExpanded(prev => ({ ...prev, [folderPath]: tree[folderPath] }));
      setFocusedFolder(folderPath);
      return;
    }

    // Lazy-load children for this folder
    if (loadingFolders.has(folderPath)) return; // already loading

    setLoadingFolders(prev => new Set(prev).add(folderPath));
    try {
      const children = await fetchChildren(folderPath);
      if (children.length > 0) {
        setTreeExpanded(prev => ({ ...prev, [folderPath]: children }));
      }
      setFocusedFolder(folderPath);
    } finally {
      setLoadingFolders(prev => {
        const next = new Set(prev);
        next.delete(folderPath);
        return next;
      });
    }
  };

  // ── Select node ────────────────────────────────────────────
  const handleTimelineDateChange = useCallback((filePath) => {
    setTimelineCurrentPath(filePath);
  }, []);

  // ── Outbox-first read for edit ─────────────────────────────
  // Returns the file's latest content + concurrency token, preferring a
  // pending outbox entry (dirty/conflict) over the backend read-through.
  const readFileForEdit = useCallback(async (path) => {
    const ob = await outboxGetForRead(path);
    if (ob) {
      return {
        content: ob.content,
        sha: ob.baseSha ?? null,
        baseContent: ob.baseContent ?? ob.content,
        exists: true,
        readFailed: false,
        state: ob.state ?? null,
        remoteContent: ob.remoteContent ?? null,
        remoteSha: ob.remoteSha ?? null,
      };
    }
    try {
      const result = await fileGetContent(path);
      if (result.exists) {
        return {
          content: result.content || '',
          sha: result.sha || null,
          baseContent: result.content || '',
          exists: true,
          readFailed: false,
          state: null,
          remoteContent: null,
          remoteSha: null,
        };
      }
    } catch {
      // Offline / read failure — signal it so callers don't treat "can't read"
      // as "file doesn't exist" and overwrite the remote file with empty content.
      return { content: '', sha: null, baseContent: '', exists: false, readFailed: true, state: null, remoteContent: null, remoteSha: null };
    }
    return { content: '', sha: null, baseContent: '', exists: false, readFailed: false, state: null, remoteContent: null, remoteSha: null };
  }, []);

  const selectNode = useCallback(async (path, explicitType) => {
    // Navigating to any node leaves the conflicts view (it only overlays the
    // right panel; a tree click should drop straight into the node).
    setConflictView(null);
    setResolvingConflict(null);
    setTreeFocusPath(null);
    setNodeOffline(false);
    setInboxOffline(false);
    baseUnknownRef.current = false;

    // Timeline folder — view with the TimelineNode continuous-scroll component.
    if (explicitType === 'timeline') {
      setSelectedNode({ path, type: 'timeline' });
      setBlocks([]);
      setFileSha(null);
      setBaseContent(null);
      setConflictInfo(null);
      setSyncState(null);
      setBlockLoading(false);
      setNodeNotFound(false);
      setLastUpdatedAt(null);
      setLeftWidth(PANEL_MIN);
      // Clear the persisted active node so the previous file isn't
      // restored on next visit — Timeline is always a fresh entry point.
      setActiveNode('').catch(() => {});
      return;
    }

    // Regular markdown file — open as a block card list.
    setSelectedNode({ path, type: 'file' });

    // Persist active node so it's restored on next visit
    setActiveNode(path).catch(() => {});

    setBlockLoading(true);
    setLastUpdatedAt(null);
    try {
      const read = await readFileForEdit(path);
      rawContentRef.current = read.content;
      lastPulledAtRef.current = Date.now();

      const isLocalOnly = read.state === 'local-only';

      if (read.exists) {
        setLastUpdatedAt(Date.now());
        setBlocks(parseBlocks(read.content || ''));
        setFileSha(read.sha || null);
        setBaseContent(read.baseContent || '');
        setConflictInfo(read.state === 'conflict'
          ? { remoteContent: read.remoteContent, remoteSha: read.remoteSha }
          : null);
        // Sync state is set by the `selectedNode` effect (cross-device aware)
        // — not here — to avoid racing the backend status lookup.
        setNodeNotFound(false);
        if (isInboxPath(path) && isLocalOnly) {
          setInboxOffline(true);
          baseUnknownRef.current = true;
        }
      } else if (read.readFailed) {
        // Offline / read failure — NOT "file doesn't exist".  Never auto-create
        // or overwrite: an unseen remote file must not be clobbered.
        setBlocks([]);
        setFileSha(null);
        setBaseContent('');
        setConflictInfo(null);
        setSyncState(null);
        if (isInboxPath(path)) {
          // Allow additive offline editing; the first save becomes a
          // `local-only` entry that is merged (not overwritten) when back online.
          setInboxOffline(true);
          baseUnknownRef.current = true;
          setNodeNotFound(false);
        } else {
          setNodeOffline(true);
        }
      } else {
        // Genuinely missing file.
        setBlocks([]);
        setFileSha(null);
        setBaseContent('');
        setConflictInfo(null);
        setSyncState(null);
        // Auto-create inbox.md if it doesn't exist yet (first-time inbox access)
        if (isInboxPath(path)) {
          try {
            await saveFileLocal(path, '', '', null);
            setNodeNotFound(false);
          } catch (err) {
            // Not "file not found" — surface the real reason (e.g. no data
            // source configured) instead of a misleading "deleted" message.
            toast('error', err?.message || 'Failed to create inbox.', 6000);
            setNodeNotFound(false);
          }
        } else {
          setNodeNotFound(true);
        }
      }
    } catch (err) {
      console.error(`Failed to load "${path}":`, err);
      rawContentRef.current = '';
      setBlocks([]);
      setFileSha(null);
      setBaseContent('');
      setConflictInfo(null);
      setSyncState(null);
      setNodeNotFound(false);
      // An unexpected load error is treated like an offline read failure,
      // never as "not found" (which would mislead or, for inbox, clobber).
      if (isInboxPath(path)) {
        setInboxOffline(true);
        baseUnknownRef.current = true;
      } else {
        setNodeOffline(true);
      }
    } finally {
      setBlockLoading(false);
    }

    // Auto-collapse left panel
    setLeftWidth(PANEL_MIN);
  }, [readFileForEdit, toast, isInboxPath]);

  // ── Prefetch active node ─────────────────────────────────
  // Fire immediately on mount so getActiveNode runs in parallel with
  // tree + settings (Frame 1). The result is consumed by the auto-load
  // effect below once the data source is confirmed.
  useEffect(() => {
    if (activeNodePromise.current) return;
    activeNodePromise.current = getActiveNode()
      .then(res => res.active_node || '')
      .catch(() => '');
  }, []);

  // ── Auto-load last active node on first load ─────────────
  // After the data source is confirmed, restore the last active node.
  // Tree + settings are NOT waited on — content loads independently.
  useEffect(() => {
    if (!dataSourceReady) return;
    if (initialLoadDoneRef.current) return;

    initialLoadDoneRef.current = true;

    (activeNodePromise.current || Promise.resolve(''))
      .then(active => {
        if (active) {
          // Restore last active file (inbox.md or any .md node)
          selectNode(active);
        } else {
          // No active node — default to inbox
          selectNode(inboxPath);
        }
      })
      .catch(() => {});
  }, [dataSourceReady, selectNode, inboxPath]);

  // ── Restore persisted focused folder (expanded branch) on first load ──
  // Re-expand the stored branch and re-mark its deepest folder as focused.
  useEffect(() => {
    if (!dataSourceReady || treeLoading || !initialLoadDoneRef.current) return;
    if (focusedFolderRestoredRef.current) return;
    focusedFolderRestoredRef.current = true;

    const stored = localStorage.getItem('julynote_focused_folder');
    if (!stored) return;

    (async () => {
      let prefix = '';
      for (const seg of stored.split('/').filter(Boolean)) {
        prefix = prefix ? `${prefix}/${seg}` : seg;
        let children = tree[prefix];
        if (!children) {
          children = await fetchChildren(prefix);
        }
        // Missing folder (deleted / different user) — abort and stay at root.
        if (!children || children.length === 0) return;
        setTreeExpanded(prev => ({ ...prev, [prefix]: children }));
      }
      setFocusedFolder(stored);
    })();
  }, [dataSourceReady, treeLoading, tree, fetchChildren]);

  // ── Block CRUD (same pattern as V1 Node.jsx) ──────────────
  const openNewBlock = () => {
    setEditingIndex(null);
    setModalInitial('');
    setModalOpen(true);
  };

  const openEditBlock = (index, content) => {
    setEditingIndex(index);
    setModalInitial(content);
    setModalOpen(true);
  };

  // ── Fire-and-forget save helpers ───────────────────────────
  const selectedNodeRef = useRef(selectedNode);
  useEffect(() => { selectedNodeRef.current = selectedNode; }, [selectedNode]);

  const reorderTimerRef = useRef(null);

  // Single source of truth for the timeline folder.  The mark actions write
  // here and the timeline view reads here (resolveTimelineFolderPath returns
  // the timeline node's path, selected below with this same value) — keep
  // them in sync, and respect the notes root like inbox does.
  const timelineFolder = notesRoot ? `${notesRoot}/Timeline` : 'Timeline';

  // Timeline icon dot state — a folder, so it's "busy" when any child under it
  // is saving/uploading or conflicted.  Conflict wins over saving.
  const timelineStatus = useMemo(() => {
    const prefix = timelineFolder + '/';
    if ((conflicts || []).some((c) => c.path.startsWith(prefix))) return 'conflict';
    if ([...savingPaths].some((p) => p.startsWith(prefix))) return 'saving';
    return null;
  }, [timelineFolder, conflicts, savingPaths]);

  const saveAndMirror = useCallback(async (path, content, sha, version, isReorder = false, timeline = null, baseContent = null) => {
    // Write-ahead: persist locally first, then the outbox sync loop pushes.
    // The first edit captures its diff baseline from `baseContent` (or the
    // currently-viewed file's latest raw content); later edits reuse the
    // entry's original base.  baseContent state is intentionally NOT updated
    // here — it stays "last synced" so the block badge stays lit until sync.
    const base = baseContent ?? rawContentRef.current ?? content;
    // A save of the *viewed* file while its remote base is unknown must stay
    // `local-only` (never auto-push) until reconciled.  Saves of other files
    // (move/copy targets) are unaffected.
    const baseUnknown = path === selectedNodeRef.current?.path && baseUnknownRef.current;
    try {
      return await saveFileLocal(path, content, base, sha, timeline, baseUnknown).then((entry) => {
        rawContentRef.current = content;
        return entry;
      });
    } catch (err) {
      // A local write failure (e.g. storage full / plugin error) would
      // otherwise be silent — surface a generic notice so the user knows
      // their edit was not saved.
      toast('error', 'Save failed. Please try again.', 4000);
      return null;
    }
  }, [toast]);

  // ── Outbox subscription: dirty-path badges + sha/base refresh on sync ──
  const refreshSavingPaths = useCallback(async () => {
    const list = await outboxList();
    // In-flight set — files actively uploading (dirty → server) or saving
    // (syncing → Git).  Conflict entries are excluded: they're parked and
    // surfaced separately via the pinned Conflicts entry.
    setSavingPaths(new Set(
      list
        .filter((e) => e.state === 'dirty' || e.state === 'syncing')
        .map((e) => e.filePath)
    ));
  }, []);

  // Track whether the currently-viewed file is in conflict (and keep its
  // remote snapshot) so the block list can flag the diverged blocks.
  const refreshConflictInfo = useCallback(async () => {
    const path = selectedNodeRef.current?.path;
    if (!path) { setConflictInfo(null); return; }
    const entry = await outboxGet(path);
    if (entry && entry.state === 'conflict') {
      setConflictInfo({ remoteContent: entry.remoteContent ?? null, remoteSha: entry.remoteSha ?? null });
    } else {
      setConflictInfo(null);
    }
  }, []);

  // Track whether the currently-viewed file has an in-flight save (dirty →
  // syncing) so the header can show a live sync status instead of the time.
  const refreshSyncState = useCallback(async () => {
    const path = selectedNodeRef.current?.path;
    if (!path) { setSyncState(null); savingStampRef.current = null; return; }

    // Resolve the raw state: a local outbox entry wins; otherwise ask the
    // backend so another device's in-flight save still shows as "Syncing…"
    // here (cross-device awareness).  A backend "syncing" means a dirty
    // entry exists on the server.
    let raw = null; // 'dirty' | 'syncing' | null
    const entry = await outboxGet(path);
    if (entry && (entry.state === 'dirty' || entry.state === 'syncing')) {
      raw = entry.state;
    } else {
      try {
        const res = await fileStatus([path]);
        const item = (res.items || []).find((i) => i.path === path);
        raw = item && item.state === 'syncing' ? 'syncing' : null;
      } catch {
        return; // Offline — leave the current badge as-is.
      }
    }

    if (raw === 'dirty') {
      if (!savingStampRef.current || savingStampRef.current.path !== path) {
        savingStampRef.current = { path, at: Date.now() };
      }
      setSyncState('dirty');
      return;
    }

    // Hold the "Saving…" (upload) phase for the minimum window before
    // advancing to "Syncing…" or idle, so the upload phase is actually seen.
    const stamp = savingStampRef.current;
    const shownFor = stamp && stamp.path === path ? Date.now() - stamp.at : Infinity;
    if (shownFor < MIN_SAVING_DISPLAY_MS) {
      setSyncState('dirty');
      if (savingTimerRef.current == null) {
        savingTimerRef.current = setTimeout(() => {
          savingTimerRef.current = null;
          refreshSyncState();
        }, MIN_SAVING_DISPLAY_MS - shownFor);
      }
      return;
    }

    if (stamp && stamp.path === path) savingStampRef.current = null;
    setSyncState(raw);
  }, []);

  // Cross-device sync state — re-check the viewed node's server status on
  // selection so another device's in-flight save shows as "Syncing…" even
  // though this device has no local outbox entry for it.
  useEffect(() => {
    if (!selectedNode || selectedNode.type !== 'file') { setSyncState(null); return; }
    refreshSyncState();
  }, [selectedNode, refreshSyncState]);

  // Clear any pending "Saving…" min-display re-check timer on unmount.
  useEffect(() => () => {
    if (savingTimerRef.current) clearTimeout(savingTimerRef.current);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  // ── Conflict list (device outbox conflicts + server external conflicts) ──
  const refreshConflicts = useCallback(async () => {
    setConflictsLoading(true);
    try {
      const [externalRes, outboxEntries] = await Promise.all([
        fileConflicts().catch(() => ({ conflicts: [] })),
        outboxList(),
      ]);
      const deviceConflicts = outboxEntries
        .filter((e) => e.state === 'conflict')
        .map((e) => ({ path: e.filePath, type: 'device' }));
      const externalConflicts = (externalRes.conflicts || []).map((c) => ({ path: c.path, type: 'external' }));
      const seen = new Set();
      const merged = [...deviceConflicts, ...externalConflicts].filter((c) => {
        const k = `${c.type}:${c.path}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      // Best-effort per-conflict block count (for the inbox badge).  Null on
      // two-way conflicts or when the three-way inputs can't be read.
      const withCounts = await Promise.all(merged.map(async (c) => {
        let blockCount = null;
        try {
          let base, ours, theirs;
          if (c.type === 'device') {
            const entry = await outboxGet(c.path);
            if (!entry) return { ...c, blockCount: null };
            base = entry.baseContent ?? null;
            ours = entry.content ?? '';
            theirs = entry.remoteContent ?? '';
          } else {
            const detail = await fileConflictDetail(c.path);
            base = detail.base_content ?? null;
            ours = detail.ours_content ?? '';
            theirs = detail.theirs_content ?? '';
          }
          if (base != null && base.trim() !== '') {
            const segs = diff3Blocks(parseBlocks(base), parseBlocks(ours), parseBlocks(theirs));
            blockCount = segs.filter((s) => s.type === 'conflict').length;
          }
        } catch { /* leave blockCount null */ }
        return { ...c, blockCount };
      }));
      setConflicts(withCounts);
    } finally {
      setConflictsLoading(false);
    }
  }, []);

  // ── Conflict view handlers ─────────────────────────────────────
  const handleOpenConflicts = useCallback(() => {
    setConflictView('inbox');
    refreshConflicts();
  }, [refreshConflicts]);

  const handleCloseConflicts = useCallback(() => {
    setConflictView(null);
    setResolvingConflict(null);
  }, []);

  const handleSelectConflict = useCallback((conflict) => {
    setResolvingConflict(conflict);
    setConflictView('resolve');
  }, []);

  // Open the conflict resolve view for the currently-viewed node (used by the
  // block conflict badge — that badge only appears for a device conflict).
  const handleOpenCurrentConflict = useCallback(() => {
    const path = selectedNodeRef.current?.path;
    if (!path) return;
    setResolvingConflict({ path, type: 'device' });
    setConflictView('resolve');
  }, []);

  const handleConflictResolved = useCallback(() => {
    refreshConflicts();
    refreshSavingPaths();
    setResolvingConflict(null);
    setConflictView('inbox');
  }, [refreshConflicts, refreshSavingPaths]);

  const handleConflictBackToList = useCallback(() => {
    setResolvingConflict(null);
    setConflictView('inbox');
    refreshConflicts();
  }, [refreshConflicts]);

  useEffect(() => {
    refreshSavingPaths();
    refreshConflictInfo();
    refreshSyncState();
    const unsub = subscribe((payload) => {
      if (payload.type === 'synced' || payload.type === 'changed') {
        refreshSavingPaths();
        refreshConflictInfo();
        refreshSyncState();
      }
      if (payload.type === 'synced' && selectedNodeRef.current?.path === payload.path) {
        if (payload.sha != null) setFileSha(payload.sha);
        setBaseContent(payload.content);
      }
      if (payload.type === 'rate-limited') {
        toast('warning', 'Gitee is rate-limited — retrying later. Your changes are safe on this device.', 8000);
      }
      if (payload.type === 'paused') {
        toast('error', 'Sync paused after repeated failures. Your changes are saved locally.', 8000);
      }
    });
    return unsub;
  }, [refreshSavingPaths, refreshConflictInfo, refreshSyncState, toast]);

  // ── Conflict freshness — pull on mount, on foreground, and on focus ──
  useEffect(() => {
    if (!dataSourceReady) return;
    refreshConflicts();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshConflicts(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [dataSourceReady, refreshConflicts]);

  // ── Foreground status poll — resolve in-flight (syncing) saves ──
  // While any outbox entry is `syncing`, poll /file/status to resolve it
  // into `synced` (delete) or `conflict` (external edit).  Also re-checks the
  // viewed node's server status so a cross-device "Syncing…" badge appears
  // while another device is saving and clears once it finishes.  Polls at the
  // backend's debounce cadence (see SYNC_STATUS_POLL_MS) — not real-time.
  useEffect(() => {
    if (!dataSourceReady) return;
    let timer;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      const list = await outboxList();
      const syncing = list.filter((e) => e.state === 'syncing');
      if (syncing.length > 0) {
        try {
          const res = await fileStatus(syncing.map((e) => e.filePath));
          await applyFileStatuses(res.items || []);
          refreshSavingPaths();
          if ((res.items || []).some((i) => i.state === 'conflict')) {
            refreshConflicts();
          }
        } catch { /* network hiccup — retry next poll */ }
      }
      // Keep the viewed node's sync badge cross-device aware.
      if (selectedNodeRef.current?.path) {
        refreshSyncState();
      }
    };
    // Check once immediately (resolves entries already past the debounce),
    // then settle into the slow cadence.
    tick();
    timer = setInterval(tick, SYNC_STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [dataSourceReady, refreshSavingPaths, refreshConflicts, refreshSyncState]);

  const handleBlockConfirm = (content) => {
    if (!selectedNode) return Promise.resolve();
    let updated;
    if (editingIndex === null) {
      updated = [content, ...blocks];
    } else {
      updated = [...blocks];
      updated[editingIndex] = content;
    }
    const newContent = serializeBlocks(updated);
    setBlocks(parseBlocks(newContent));

    // Return the save promise — BlockEditorPanel awaits it and closes on success.
    return saveAndMirror(selectedNode.path, newContent, fileSha, null);
  };

  // ── Timeline mark actions (mark+copy / mark+move) ────────────
  // Both prepend a `YYYY-MM-DD HH:MM±HH:MM` mark line to the block, then write
  // the marked block directly into the daily timeline file as its own outbox
  // entry (the timeline file is a normal node).  They differ only in whether
  // the source keeps the block (copy) or drops it (move).
  const handleMarkBlock = useCallback((action) => async (blockText) => {
    if (!selectedNode || !blockText) return;
    const marked = applyTimeMark(blockText, buildTimeMarkLine());

    let updated;
    if (editingIndex === null) {
      // New block: copy → add marked block to source; move → source unchanged.
      updated = action === 'copy' ? [marked, ...blocks] : [...blocks];
    } else {
      updated = [...blocks];
      if (action === 'copy') {
        updated[editingIndex] = marked;
      } else {
        updated.splice(editingIndex, 1);
      }
    }

    const newContent = serializeBlocks(updated);
    setBlocks(parseBlocks(newContent));
    setModalOpen(false);
    setEditingIndex(null);

    // Source node — save without any timeline metadata.
    saveAndMirror(selectedNode.path, newContent, fileSha, null);

    // Timeline file — read local-first, prepend the marked block (newest
    // first, matching the timeline view), and save it as its own outbox
    // entry.  If the read fails (offline) and there is no local copy, block
    // instead of overwriting the remote file with empty base.
    const timelinePath = `${timelineFolder}/${localTodayStr()}.md`;
    const tl = await readFileForEdit(timelinePath);
    if (tl.readFailed) {
      toast('error', 'Offline — can\'t record to timeline. Reconnect and try again.', 6000);
      return;
    }
    const tlBlocks = parseBlocks(tl.exists ? tl.content : '');
    tlBlocks.unshift(marked);
    try {
      await saveFileLocal(timelinePath, serializeBlocks(tlBlocks), tl.content, tl.sha);
    } catch {
      toast('error', 'Failed to record to timeline.', 4000);
    }
  }, [selectedNode, editingIndex, blocks, fileSha, saveAndMirror, timelineFolder, readFileForEdit, toast]);

  const handleMarkCopy = useCallback((blockText) => handleMarkBlock('copy')(blockText), [handleMarkBlock]);
  const handleMarkMove = useCallback((blockText) => handleMarkBlock('move')(blockText), [handleMarkBlock]);

  const handleDeleteBlock = (index) => {
    if (!selectedNode) return;
    const updated = blocks.filter((_, i) => i !== index);
    const newContent = serializeBlocks(updated);
    setBlocks(parseBlocks(newContent));
    saveAndMirror(selectedNode.path, newContent, fileSha, null);
  };


  const handleClearAllBlocks = () => {
    if (!selectedNode || blocks.length === 0) return;
    setConfirmClearOpen(true);
  };

  const handleConfirmClearAllBlocks = () => {
    if (!selectedNode) return;
    setBlocks([]);
    saveAndMirror(selectedNode.path, '', fileSha, null);
    setConfirmClearOpen(false);
  };

  // ── Reconcile a local-only inbox edit with the remote file ──
  // When the inbox failed to load (offline) the user can still add blocks;
  // those live in a `local-only` outbox entry.  Once reachable, prepend the
  // local new blocks to the freshly-read remote content (no dedup — the
  // offline hint already told the user the existing content wasn't loaded)
  // and push with the correct remote sha.
  const reconcileInbox = useCallback(async () => {
    const entry = await outboxGet(inboxPath);
    if (!entry || entry.state !== 'local-only') {
      // Nothing to merge — leave the offline hint as-is; the manual retry
      // button reloads via selectNode.
      return;
    }

    try {
      const result = await fileGetContent(inboxPath);
      if (!result.exists) {
        // Remote doesn't exist yet — the local-only content is the whole file.
        entry.state = 'dirty';
        entry.baseUnknown = false;
        entry.baseSha = null;
        entry.baseContent = '';
        await outboxPut(inboxPath, entry);
        baseUnknownRef.current = false;
        setInboxOffline(false);
        setBaseContent('');
        runSync();
        return;
      }

      const remoteContent = result.content || '';
      const remoteSha = result.sha || null;
      const localBlocks = parseBlocks(entry.content || '');
      const remoteBlocks = parseBlocks(remoteContent);
      const merged = serializeBlocks([...localBlocks, ...remoteBlocks]);

      entry.content = merged;
      entry.baseSha = remoteSha;
      entry.baseContent = remoteContent;
      entry.baseUnknown = false;
      entry.state = 'dirty';
      await outboxPut(inboxPath, entry);

      baseUnknownRef.current = false;
      setInboxOffline(false);
      setFileSha(remoteSha);
      setBaseContent(remoteContent);
      setBlocks(parseBlocks(merged));
      runSync();
    } catch {
      // Still offline / read failed — keep local-only and try again later.
    }
  }, [inboxPath]);

  // ── Offline recovery: drain any pending entries on mount / reconnect ──
  useEffect(() => {
    runSync();
    const onOnline = () => { resetSyncBackoff(); runSync(); reconcileInbox(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [reconcileInbox]);

  // ── Auto-reconcile the inbox when it enters local-only mode ──
  // Re-opening an inbox that has a local-only entry (or entering offline mode)
  // attempts a merge.  reconcileInbox is a no-op when there is nothing to merge
  // or the remote read still fails, so this cannot loop.
  useEffect(() => {
    if (inboxOffline) reconcileInbox();
  }, [inboxOffline, reconcileInbox]);

  // Manual retry from the offline hint: merge a local-only entry if present,
  // otherwise reload the inbox from the server.
  const handleInboxRetry = useCallback(async () => {
    const entry = await outboxGet(inboxPath);
    if (entry && entry.state === 'local-only') {
      await reconcileInbox();
    } else {
      selectNode(inboxPath);
    }
  }, [inboxPath, reconcileInbox, selectNode]);

  // ── Pull-to-refresh: re-load the node content from the server ──
  // Pass refresh: true to bypass the backend cache and read from Git,
  // so external edits (another device / GitHub web) become visible.
  // `refreshingRef` guards against concurrent refreshes so a repeated
  // pull while a fetch is in flight is ignored.
  const handlePullToRefresh = useCallback(async () => {
    if (!selectedNode || selectedNode.type !== 'file') return;
    if (refreshingRef.current) return; // refresh already in flight
    const path = selectedNode.path;

    // Never clobber unsynced local edits — a pending outbox entry (dirty →
    // server, or syncing → Git) means re-reading Git here would return stale
    // content and wipe the local view.
    const ob = await outboxGetForRead(path);
    if (ob) {
      toast('info', 'Unsynced changes — refresh skipped.', 3000);
      return;
    }

    refreshingRef.current = true;
    setRefreshing(true);
    const startedAt = Date.now();
    try {
      const result = await fileGetContent(path, { refresh: true });
      if (result.exists) {
        rawContentRef.current = result.content || '';
        lastPulledAtRef.current = Date.now();
        setLastUpdatedAt(Date.now());
        setBlocks(parseBlocks(result.content || ''));
        setFileSha(result.sha || null);
        setBaseContent(result.content || '');
        setNodeOffline(false);
        setInboxOffline(false);
        baseUnknownRef.current = false;
        if (result.note) {
          toast('warning', result.note, 4000);
          // The backend refused to overwrite a dirty entry (another device's
          // in-flight save) — surface that as the live sync status so the
          // "skipped" message isn't unexplained.
          setSyncState('syncing');
        }
      } else {
        setBlocks([]);
        setFileSha(null);
        setBaseContent('');
        setNodeOffline(false);
        setInboxOffline(false);
        baseUnknownRef.current = false;
        setNodeNotFound(true);
      }
    } catch (err) {
      toast('error', `Refresh failed: ${err.message}`, 4000);
    } finally {
      // Hold the "Refreshing…" indicator for a minimum time so it doesn't
      // flash by invisibly when the read resolves on a warm cache.  Keep the
      // in-flight guard set until the indicator actually hides so a second
      // pull can't start a refresh that the first one's timer would then
      // wrongly dismiss.
      const wait = Math.max(0, MIN_REFRESH_DISPLAY_MS - (Date.now() - startedAt));
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        refreshingRef.current = false;
        setRefreshing(false);
      }, wait);
    }
  }, [selectedNode, toast]);

  // ── Foreground re-pull: defeat backend idle eviction on resume ──
  // The backend expires a user's clean cache entries after 300 s idle.  When
  // the app returns to the foreground after longer than that, re-read with
  // refresh=true so the view isn't serving a just-evicted (stale) buffer.
  const IDLE_TTL_MS = 300 * 1000;
  useEffect(() => {
    const maybeRefresh = async () => {
      const node = selectedNodeRef.current;
      if (!node || node.type !== 'file') return;
      if (Date.now() - lastPulledAtRef.current < IDLE_TTL_MS) return;
      const ob = await outboxGetForRead(node.path);
      if (ob) return; // unsynced local edits — never clobber them
      handlePullToRefresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [handlePullToRefresh]);

  const handleModalDelete = () => {
    if (editingIndex !== null) {
      handleDeleteBlock(editingIndex);
    }
    setModalOpen(false);
    setEditingIndex(null);
  };

  const handleReorderEnd = useCallback((fromIndex, toIndex) => {
    if (!selectedNode) return;
    if (fromIndex === toIndex) return;

    const updated = [...blocks];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    const newContent = serializeBlocks(updated);
    setBlocks(parseBlocks(newContent));

    // Debounce 250ms — only the last reorder in a rapid sequence saves
    if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
    reorderTimerRef.current = setTimeout(() => {
      saveAndMirror(selectedNode.path, newContent, fileSha, null, true);
    }, 250);
  }, [blocks, selectedNode, fileSha, saveAndMirror]);

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingIndex(null);
  };

  // ── Help popup ──────────────────────────────────────────────
  const handleOpenHelp = () => {
    setHelpOpen(true);
  };

  // ── Block auto-save settings ──────────────────────────────
  const handleSaveBlockSettings = async (word, delay) => {
    setBlockAutoSaveWord(word);
    setBlockAutoSaveDelay(delay);
    try {
      await saveUserBlockSettings(word, delay);
      toast('success', 'Block auto-save settings saved.', 3000);
    } catch (e) {
      toast('error', `Failed to save: ${e.message}`, 4000);
      throw e;
    }
  };

  // ── Move mode ─────────────────────────────────────────────
  const startMove = (blockIndex) => {
    if (!selectedNode) return;
    setMoveSource({
      filePath: selectedNode.path,
      blockIndex,
      blockContent: blocks[blockIndex],
    });
    setMoveMode(true);
    // Don't auto-expand the left panel — user can expand it manually.
    // This matches the swipe-left behavior which also enters move mode
    // without opening the node panel.
  };

  const cancelMove = useCallback(() => {
    setMoveMode(false);
    setMoveSource(null);
    setContextMenu({ show: false, x: 0, y: 0, targetPath: '' });
    setFilenamePrompt({ show: false, targetPath: '' });
    setConfirmBlockDelete(null);
    setLeftWidth(PANEL_MIN);
  }, []);

  // Esc to cancel move
  useEffect(() => {
    if (!moveMode) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        cancelMove();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [moveMode, cancelMove]);

  // ── Bidirectional swipe callbacks ──────────────────────────
  // Left-drag (toward sidebar) → move mode (sidebar targets)
  // Right-drag (away from sidebar) → block delete confirmation

  const handleSwipeProgress = useCallback((direction, ratio) => {
    // Left-drag → glow sidebar when committed
    setDragCommit(direction === 'left' && ratio >= COMMIT_RATIO);
    // Right-drag → full-screen right-edge border (bottom → top)
    if (direction === 'right') {
      setScreenSwipe({ ratio, committed: ratio >= COMMIT_RATIO });
    } else {
      setScreenSwipe(null);
    }
  }, []);

  const handleSwipeCommit = useCallback((direction, filePath, blockIndex, content) => {
    if (direction === 'left') {
      // Enter move mode and auto-open the left panel
      setMoveSource({ filePath, blockIndex, blockContent: content });
      setMoveMode(true);
      setLeftWidth(PANEL_MAX);
    } else if (direction === 'right') {
      // Open block delete confirmation from right
      setConfirmBlockDelete({ filePath, blockIndex });
    }
    setDragCommit(false);
    setScreenSwipe(null);
  }, []);

  const handleConfirmBlockDelete = useCallback(async () => {
    if (!confirmBlockDelete) return;
    const { filePath, blockIndex } = confirmBlockDelete;

    // Use blocks state as source of truth when modifying the currently
    // viewed file; otherwise read outbox-first (read-through).
    if (selectedNode && selectedNode.path === filePath) {
      const updated = blocks.filter((_, i) => i !== blockIndex);
      const newContent = serializeBlocks(updated);
      setBlocks(updated);
      saveAndMirror(filePath, newContent, fileSha, null);
    } else {
      const source = await readFileForEdit(filePath);
      if (source.exists) {
        const sourceBlocks = parseBlocks(source.content || '');
        if (blockIndex < sourceBlocks.length) {
          sourceBlocks.splice(blockIndex, 1);
          const newContent = serializeBlocks(sourceBlocks);
          saveAndMirror(filePath, newContent, source.sha, null, false, null, source.content);
        }
      }
    }

    toast('info', 'Block deleted');
    setConfirmBlockDelete(null);
  }, [confirmBlockDelete, selectedNode, toast, blocks, fileSha, saveAndMirror, readFileForEdit]);

  // Discard the block being moved (delete from source) — called from left panel during move mode
  const handleDiscardBlock = useCallback(async () => {
    if (!moveSource) return;
    const { filePath, blockIndex } = moveSource;

    // Use blocks state as source of truth when modifying the currently
    // viewed file; otherwise read outbox-first (read-through).
    if (selectedNode && selectedNode.path === filePath) {
      const updated = blocks.filter((_, i) => i !== blockIndex);
      const newContent = serializeBlocks(updated);
      setBlocks(updated);
      saveAndMirror(filePath, newContent, fileSha, null);
    } else {
      const source = await readFileForEdit(filePath);
      if (source.exists) {
        const sourceBlocks = parseBlocks(source.content || '');
        if (blockIndex < sourceBlocks.length) {
          sourceBlocks.splice(blockIndex, 1);
          const newContent = serializeBlocks(sourceBlocks);
          saveAndMirror(filePath, newContent, source.sha, null, false, null, source.content);
        }
      }
    }

    toast('info', 'Block discarded');
    cancelMove();
  }, [moveSource, selectedNode, cancelMove, toast, blocks, fileSha, saveAndMirror, readFileForEdit]);

  // ── Drop target click → direct move ───────────────────────
  const handleDropTargetClick = (targetPath, event) => {
    if (!moveMode) return;
    event.stopPropagation();
    event.preventDefault();
    executeMove(targetPath);
  };

  // ── Quick drop (direct move from inbox or node) ─────────
  const handleQuickDrop = useCallback(async (targetPath) => {
    if (!moveSource) return;
    const { filePath: sourcePath, blockIndex, blockContent } = moveSource;

    if (sourcePath === targetPath) {
      toast('error', 'Cannot move block to the same file.');
      cancelMove();
      return;
    }

    // Inbox → Node or Node → Node: move (remove from source)
    const source = await readFileForEdit(sourcePath);
    let newSourceContent = null;
    if (source.exists) {
      const sourceBlocks = parseBlocks(source.content || '');
      if (blockIndex < sourceBlocks.length) {
        sourceBlocks.splice(blockIndex, 1);
        newSourceContent = serializeBlocks(sourceBlocks);
        saveAndMirror(sourcePath, newSourceContent, source.sha, null, false, null, source.content);
      }
    }

    // Fetch target content (outbox-first), then prepend
    const target = await readFileForEdit(targetPath);
    if (target.readFailed) {
      toast('error', 'Offline — can\'t read the destination. Reconnect and try again.', 6000);
      cancelMove();
      return;
    }
    const targetBlocks = parseBlocks(target.exists ? target.content : '');
    targetBlocks.unshift(blockContent);
    const newTargetContent = serializeBlocks(targetBlocks);
    saveAndMirror(targetPath, newTargetContent, target.sha, null, false, null, target.content);

    // Refresh current view
    if (selectedNode && selectedNode.path === sourcePath && newSourceContent != null) {
      setBlocks(parseBlocks(newSourceContent));
    }

    toast('success', `Moved block to ${targetPath}`);
    cancelMove();
  }, [moveSource, readFileForEdit, saveAndMirror, selectedNode, cancelMove, toast]);

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, targetPath: '' });
  };

  // ── Move / Copy / Make new node ───────────────────────────
  const executeMove = async (targetPath) => {
    if (!moveSource) return;
    const { filePath: sourcePath, blockIndex, blockContent } = moveSource;

    if (sourcePath === targetPath) {
      toast('error', 'Cannot move block to the same file.');
      cancelMove();
      return;
    }

    // If target is a folder (not a .md file), prompt for new filename
    if (!targetPath.endsWith('.md')) {
      setNewNodeOp('move');
      setFilenamePrompt({ show: true, targetPath });
      setNewFilename('untitled.md');
      setContextMenu({ show: false, x: 0, y: 0, targetPath: '' });
      return;
    }

    // Read the target FIRST — if we can't (offline) and there is no local
    // copy, abort before removing the block from the source, so the block is
    // never lost.
    const target = await readFileForEdit(targetPath);
    if (target.readFailed) {
      toast('error', 'Offline — can\'t read the destination. Reconnect and try again.', 6000);
      cancelMove();
      return;
    }

    // Remove from source (Inbox or Node — same behavior: move, not copy).
    // Use `blocks` state as the source of truth when the source is the
    // currently-viewed file; otherwise read outbox-first (read-through).
    if (selectedNode && selectedNode.path === sourcePath) {
      const updated = blocks.filter((_, i) => i !== blockIndex);
      const newSourceContent = serializeBlocks(updated);
      setBlocks(updated);
      saveAndMirror(sourcePath, newSourceContent, fileSha, null);
    } else {
      const source = await readFileForEdit(sourcePath);
      if (source.exists) {
        const sourceBlocks = parseBlocks(source.content || '');
        if (blockIndex < sourceBlocks.length) {
          sourceBlocks.splice(blockIndex, 1);
          const newSourceContent = serializeBlocks(sourceBlocks);
          saveAndMirror(sourcePath, newSourceContent, source.sha, null, false, null, source.content);
        }
      }
    }

    // Prepend the block to the target and save.
    const targetBlocks = parseBlocks(target.exists ? target.content : '');
    targetBlocks.unshift(blockContent);
    const newTargetContent = serializeBlocks(targetBlocks);
    saveAndMirror(targetPath, newTargetContent, target.sha, null, false, null, target.content);

    toast('success', `Moved block to ${targetPath}`);
    cancelMove();
  };

  const executeCopy = async (targetPath) => {
    if (!moveSource) return;
    const { blockContent } = moveSource;

    // If target is a folder (not a .md file), prompt for new filename
    if (!targetPath.endsWith('.md')) {
      setNewNodeOp('copy');
      setFilenamePrompt({ show: true, targetPath });
      setNewFilename('untitled.md');
      setContextMenu({ show: false, x: 0, y: 0, targetPath: '' });
      return;
    }

    // Fetch target content (outbox-first), then prepend
    const target = await readFileForEdit(targetPath);
    if (target.readFailed) {
      toast('error', 'Offline — can\'t read the destination. Reconnect and try again.', 6000);
      cancelMove();
      return;
    }
    const targetBlocks = parseBlocks(target.exists ? target.content : '');
    targetBlocks.unshift(blockContent);
    const newTargetContent = serializeBlocks(targetBlocks);
    saveAndMirror(targetPath, newTargetContent, target.sha, null, false, null, target.content);

    toast('success', `Copied block to ${targetPath}`);
    cancelMove();
  };

  // ── Send block from inbox to a node ───────────
  const handleSendToNode = useCallback(async (sourcePath, blockIndex, content, targetPath, opts = {}) => {
    const { skipSourceRemoval = false } = opts;
    if (!sourcePath || !content || !targetPath) return;

    // Copy to target node — read (outbox-first), prepend, save.
    const target = await readFileForEdit(targetPath);
    if (target.readFailed) {
      toast('error', 'Offline — can\'t read the destination. Reconnect and try again.', 6000);
      return;
    }
    const targetBlocks = parseBlocks(target.exists ? target.content : '');
    targetBlocks.unshift(content);
    const newTargetContent = serializeBlocks(targetBlocks);
    saveAndMirror(targetPath, newTargetContent, target.sha, null, false, null, target.content);

    // Remove block from source (move, not copy).
    // Skip when caller already handled source removal (e.g. handleFileMoveToDestination).
    if (!skipSourceRemoval && blockIndex !== null) {
      const source = await readFileForEdit(sourcePath);
      if (source.exists) {
        const sourceBlocks = parseBlocks(source.content || '');
        if (blockIndex < sourceBlocks.length) {
          sourceBlocks.splice(blockIndex, 1);
          const newSourceContent = serializeBlocks(sourceBlocks);
          saveAndMirror(sourcePath, newSourceContent, source.sha, null, false, null, source.content);
        }
      }
    }

    toast('success', `Sent block to ${targetPath}`);
  }, [readFileForEdit, saveAndMirror, toast]);

  // ── File-view modal move-to-destination: save content, close modal, then route ──
  const handleFileMoveToDestination = useCallback((dest, blockText) => {
    if (!selectedNode || !blockText) return;

    // 1. Save/update the block content first
    let updated;
    if (editingIndex === null) {
      updated = [blockText, ...blocks];
    } else {
      updated = [...blocks];
      updated[editingIndex] = blockText;
    }
    const newContent = serializeBlocks(updated);
    setBlocks(parseBlocks(newContent));

    const srcIndex = editingIndex !== null ? editingIndex : 0;

    // 2. Close the modal
    setModalOpen(false);
    setEditingIndex(null);

    // 3. Route to destination
    if (dest.type === 'nodes') {
      // Enter move mode — user still picks target
      saveAndMirror(selectedNode.path, newContent, fileSha, null);
      setMoveSource({
        filePath: selectedNode.path,
        blockIndex: srcIndex,
        blockContent: blockText,
      });
      setMoveMode(true);
      setLeftWidth(PANEL_MAX);
    } else if (dest.type === 'node' && dest.path) {
      // Move block directly to the QA node — remove from source, then send
      const withoutBlock = [...updated];
      withoutBlock.splice(srcIndex, 1);
      const sourceContent = serializeBlocks(withoutBlock);
      setBlocks(parseBlocks(sourceContent));
      saveAndMirror(selectedNode.path, sourceContent, fileSha, null);
      handleSendToNode(selectedNode.path, srcIndex, blockText, dest.path, { skipSourceRemoval: true });
    }
  }, [selectedNode, editingIndex, blocks, fileSha, saveAndMirror, handleSendToNode]);

  const executeMakeNewNode = (targetPath) => {
    // targetPath is a folder path or file path — prompt for filename
    setNewNodeOp('move');
    setFilenamePrompt({ show: true, targetPath });
    setNewFilename('untitled.md');
  };

  // Direct entry point from left-panel "New node..." placeholder
  // — bypasses the context menu when the tree is empty.
  const handleMakeNewNodeAtPath = (targetPath) => {
    setNewNodeOp('move');
    setFilenamePrompt({ show: true, targetPath });
    setNewFilename('untitled');
  };

  // ── Add node (via + button on section label) ──────────────
  const handleOpenAddNode = (targetPath) => {
    setAddNodePopup({ show: true, targetPath });
    setAddNodeName('');
    setAddNodeType('file');
    setAddNodeError('');
  };

  const handleConfirmAddNode = async () => {
    const { targetPath } = addNodePopup;
    const name = addNodeName.trim();
    if (!name || addNodeSubmitting) return;

    const isFolder = addNodeType === 'folder';
    const fname = isFolder ? name : (name.endsWith('.md') ? name : name + '.md');
    const fullPath = targetPath ? `${targetPath}/${fname}` : fname;

    // No full-screen runLocked here — the create is optimistic, so the new
    // node/folder appears in the tree immediately.  Show local loading on the
    // button while the (still synchronous) git write completes.
    setAddNodeSubmitting(true);
    try {
      if (isFolder) {
        await createFolder(fullPath);
        toast('success', `Created folder: ${name}`);
      } else {
        // Empty content — the backend stores a minimal placeholder (git APIs
        // reject truly empty files) and reads it back as "" so the node opens
        // with the "No blocks yet" empty state.
        await createNode(fullPath, 'markdown', '');
        toast('success', `Created node: ${name}`);
      }
      setAddNodePopup({ show: false, targetPath: '' });
    } catch (err) {
      setAddNodeError(err.message || 'Failed to create');
    } finally {
      setAddNodeSubmitting(false);
    }
  };

  // ── Unified node/folder settings (rename / icon / quick access / hide / delete) ──
  const handleNodeSettings = (path, name, type) => {
    setNodeOptions({ path, name, type });
  };

  const handleCloseNodeOptions = () => {
    setNodeOptions(null);
  };

  // Expand the panel and reveal a folder in the tree, expanding any collapsed
  // ancestors first so the folder becomes visible. Single-path: collapses
  // sibling branches and marks the revealed folder as focused.
  const expandToFolder = async (folderPath) => {
    setLeftWidth(PANEL_MAX);
    const segments = folderPath.split('/').filter(Boolean);
    setTreeExpanded(prev => {
      const next = {};
      for (const key of Object.keys(prev)) {
        if (folderPath.startsWith(key + '/')) next[key] = prev[key];
      }
      return next;
    });
    let prefix = '';
    for (const seg of segments) {
      prefix = prefix ? `${prefix}/${seg}` : seg;
      let children = tree[prefix];
      if (!children) {
        children = await fetchChildren(prefix);
      }
      setTreeExpanded(prev => ({ ...prev, [prefix]: children || [] }));
    }
    setFocusedFolder(folderPath);
  };

  // Quick-access folder click — open the left panel and reveal the folder.
  const handleQuickAccessFolder = (path) => {
    expandToFolder(path);
  };

  // Reveal a path in the tree: open the panel, expand only its ancestor
  // folders (collapsing the rest), and highlight it — without opening it.
  const revealInTree = async (path) => {
    setLeftWidth(PANEL_MAX);

    const segments = path.split('/').filter(Boolean);
    const ancestors = [];
    let prefix = '';
    for (let i = 0; i < segments.length - 1; i++) {
      prefix = prefix ? `${prefix}/${segments[i]}` : segments[i];
      ancestors.push(prefix);
    }

    // Collapse every folder except the ancestor chain.
    setTreeExpanded(prev => {
      const next = {};
      for (const a of ancestors) {
        if (prev[a]) next[a] = prev[a];
      }
      return next;
    });

    // Expand the ancestors, lazy-loading any that aren't cached yet.
    for (const a of ancestors) {
      let children = tree[a];
      if (!children) {
        children = await fetchChildren(a);
      }
      if (children && children.length > 0) {
        setTreeExpanded(prev => ({ ...prev, [a]: children }));
      }
    }

    setTreeFocusPath(path);

    // Focus the deepest expanded folder. If the revealed path is itself a
    // cached folder, expand and focus it; otherwise focus its parent directory.
    if (tree[path] !== undefined) {
      setTreeExpanded(prev => ({ ...prev, [path]: tree[path] }));
      setFocusedFolder(path);
    } else {
      setFocusedFolder(ancestors.length ? ancestors[ancestors.length - 1] : '');
    }
  };

  // Search-result click (node or folder) — reveal + highlight, don't open.
  const handleSearchReveal = (path) => {
    revealInTree(path);
  };

  const handleRenameNode = async (newName) => {
    if (!nodeOptions) return null;
    const oldPath = nodeOptions.path;

    // Preserve the original file extension (not just .md)
    const oldExtIdx = oldPath.lastIndexOf('.');
    const oldExt = oldExtIdx !== -1 ? oldPath.substring(oldExtIdx) : '';
    const fullNewName = oldExt && !newName.endsWith(oldExt) ? newName + oldExt : newName;

    const dir = oldPath.substring(0, oldPath.lastIndexOf('/'));
    const newPath = dir ? `${dir}/${fullNewName}` : fullNewName;

    if (oldPath === newPath) {
      setNodeOptions(null);
      return null;
    }

    // Check if a file already exists at the target path (same-directory
    // rename, so the parent folder is already in the loaded tree).
    const existsInTree = (path) => {
      if (treeData.some(item => item.path === path && item.type === 'blob')) return true;
      for (const children of Object.values(treeExpanded)) {
        if (children.some(item => item.path === path && item.type === 'blob')) return true;
      }
      return false;
    };

    // Return the error string (kept inline in the panel) instead of closing.
    if (existsInTree(newPath)) {
      return `A file named "${fullNewName}" already exists in this location.`;
    }

    // No full-screen runLocked — renameNode optimistically swaps the name in
    // the tree, then migrates any pending outbox entry + selection on success.
    // The tree is already updated, so no trailing loadTree() is needed.
    try {
      await renameNode(oldPath, newPath);

      // Migrate any pending outbox entry so unsynced edits follow the rename.
      const ob = await outboxGet(oldPath);
      if (ob) {
        await outboxRemove(oldPath);
        await outboxPut(newPath, { ...ob, filePath: newPath });
      }

      // If renamed file is currently selected, switch to new path
      if (selectedNode && selectedNode.path === oldPath) {
        setSelectedNode({ path: newPath, type: 'file' });
        setActiveNode(newPath).catch(() => {});
      }

      toast('success', `Renamed to ${fullNewName}`);
      setNodeOptions(null);
      return null;
    } catch (err) {
      return `Rename failed: ${err.message}`;
    }
  };

  const handleDeleteNode = () => {
    if (!nodeOptions) return;
    // Show confirmation popup — actual delete happens in handleConfirmDelete
    setConfirmDelete(nodeOptions);
    setNodeOptions(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { path, name, type } = confirmDelete;
    setConfirmDelete(null);

    // No full-screen runLocked — deleteNode removes the node optimistically
    // so it disappears immediately, then rolls back on failure.
    try {
      await deleteNode(path);
      // Discard any unsynced edits for the deleted file so the sync loop
      // doesn't recreate it from a stale outbox entry.
      await outboxRemove(path);

      // Collapse the folder from the expanded set if it was expanded.
      setTreeExpanded(prev => {
        if (!(path in prev)) return prev;
        const next = { ...prev };
        delete next[path];
        return next;
      });

      // If the deleted folder was the focused folder (or an ancestor of it),
      // reset the add-node target to the deleted folder's parent.
      if (type === 'folder' && (focusedFolder === path || focusedFolder.startsWith(path + '/'))) {
        setFocusedFolder(parentPath(path));
      }

      // Clear the view if the deleted node — or a node inside a deleted
      // folder — is currently selected.
      const isSelected = selectedNode
        && (selectedNode.path === path || (type === 'folder' && selectedNode.path.startsWith(path + '/')));
      if (isSelected) {
        setSelectedNode(null);
        setNodeNotFound(false);
        setBlocks([]);
        setFileSha(null);
        setBaseContent(null);
        setActiveNode('').catch(() => {});
      }

      toast('success', `Deleted ${name}`);
    } catch (err) {
      toast('error', `Delete failed: ${err.message}`);
    }
  };

  // ── "File not found" handlers ─────────────────────────────
  const handleDismissNotFound = () => {
    setSelectedNode(null);
    setNodeNotFound(false);
    setBlocks([]);
    setFileSha(null);
    setBaseContent(null);
    setActiveNode('').catch(() => {});
  };

  const handleRemoveQuickAccess = () => {
    if (!selectedNode) return;
    setNodeSetting(selectedNode.path, 'quick_access', '').then(() => {
      refreshNodeSettings();
      toast('info', 'Removed from Quick Access');
    }).catch(() => {});
    handleDismissNotFound();
  };

  const confirmNewNode = async () => {
    const { targetPath } = filenamePrompt;
    const fname = newFilename.endsWith('.md') ? newFilename : newFilename + '.md';
    // If target is a file, create in its parent directory
    const dirPath = targetPath.endsWith('.md') ? targetPath.substring(0, targetPath.lastIndexOf('/')) : targetPath;
    const fullPath = dirPath ? `${dirPath}/${fname}` : fname;

    if (!moveSource) {
      toast('error', 'Move state lost — please start the move again.');
      setFilenamePrompt({ show: false, targetPath: '' });
      return;
    }

    const { filePath: sourcePath, blockIndex, blockContent } = moveSource;

    const isMoving = newNodeOp === 'move' && sourcePath !== fullPath;

    // New file always gets created
    const newContent = serializeBlocks([blockContent]);

    // Close the prompt immediately so the UI feels responsive
    setFilenamePrompt({ show: false, targetPath: '' });
    const opLabel = newNodeOp === 'copy' ? 'copied' : 'moved';

    // Remove block from source (move, not copy-mark)
    let newSourceContent = null;
    if (isMoving) {
      // Read source content outbox-first, not from `blocks` state (the
      // currently selected node may have changed while the prompt was open).
      const source = await readFileForEdit(sourcePath);
      const sourceBlocks = parseBlocks(source.exists ? source.content : '');
      const updatedBlocks = sourceBlocks.filter((_, i) => i !== blockIndex);
      newSourceContent = serializeBlocks(updatedBlocks);
      saveAndMirror(sourcePath, newSourceContent, source.sha, null, false, null, source.content);
    }

    // Save the new file — await so we can refresh the tree afterwards.
    // baseContent '' marks the brand-new file's block as unsynced until sync.
    try {
      await saveWithRetry(fullPath, newContent, null, null, toast, false, null, '');
    } catch (_) {
      // saveWithRetry already shows toast on error — still refresh tree below
    }

    // Refresh blocks display if viewing the source file
    if (isMoving && selectedNode && selectedNode.path === sourcePath) {
      setBlocks(parseBlocks(newSourceContent || ''));
    }

    toast('success', `Created new node ${fullPath} (block ${opLabel})`);

    // Refresh the hook's tree state so the new node appears in the list.
    // loadTree() fires automatically via useEffect when tree state changes.
    await refreshNodeSettings();

    // End move mode — keep left panel open so the user can see the new node
    setMoveMode(false);
    setMoveSource(null);
    setContextMenu({ show: false, x: 0, y: 0, targetPath: '' });
  };

  // ── Click outside context menu → close ────────────────────
  useEffect(() => {
    if (!contextMenu.show) return;
    const handler = () => closeContextMenu();
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [contextMenu.show]);

  // ── Render ────────────────────────────────────────────────

  // Shared props for the block editor — passed to BlockEditorPanel
  // and the desktop modal so the two render paths never drift apart.
  const blockEditProps = {
    show: modalOpen,
    initialContent: modalInitial,
    placeholder: editingIndex === null ? 'Write something...' : undefined,
    onConfirm: handleBlockConfirm,
    onClose: handleModalClose,
    onDelete: handleModalDelete,
    destinations: blockModalDestinations,
    onMoveToDestination: handleFileMoveToDestination,
    autoSaveWord: blockAutoSaveWord,
    autoSaveDelay: blockAutoSaveDelay * 1000,
    showTimelineActions: true,
    onMarkCopy: handleMarkCopy,
    onMarkMove: handleMarkMove,
    filePath: selectedNode?.path || '',
    blockIndex: editingIndex,
    originalContent: editingIndex !== null ? blocks[editingIndex] : null,
  };

  return (
    <div className="main" style={{ padding: 0, overflow: 'visible' }}>
      <div className="v2-layout">
        {/* Left panel */}
        <NodeV2LeftPanel
          leftWidth={leftWidth}
          dragCommit={dragCommit}
          onToggle={() => setLeftWidth(p => p > PANEL_MIN ? PANEL_MIN : PANEL_MAX)}
          treeData={treeData}
          treeLoading={treeLoading}
          treeExpanded={treeExpanded}
          onToggleFolder={toggleFolder}
          selectedNode={selectedNode}
          focusPath={treeFocusPath}
          onNodeSelect={selectNode}
          onNodeSettings={handleNodeSettings}
          moveMode={moveMode}
          onDropTargetClick={handleDropTargetClick}
          onQuickDropTargetClick={handleDropTargetClick}
          onQuickAccessFolder={handleQuickAccessFolder}
          onSearchReveal={handleSearchReveal}
          onMakeNewNodeAtPath={handleMakeNewNodeAtPath}
          notesRoot={notesRoot}
          nodeIcons={nodeIcons}
          nodeDefaults={nodeDefaults}
          quickAccessNodes={quickAccessNodes}
          onAddNode={handleOpenAddNode}
          loadingFolders={loadingFolders}
          onSwipeProgress={handleSwipeProgress}
          onSwipeCancel={() => { setDragCommit(false); setScreenSwipe(null); }}
          swipeEnabled={swipeEnabled}
          onToggleSwipe={() => setSwipeEnabled(prev => !prev)}
          onOpenSettings={() => setSettingsModalOpen(true)}
          onClearBlocks={handleClearAllBlocks}
          onInboxClick={() => selectNode(inboxPath)}
          onTimelineClick={() => selectNode(timelineFolder, 'timeline')}
          isInboxActive={isInboxPath(selectedNode?.path)}
          isTimelineActive={selectedNode?.type === 'timeline'}
          savingPaths={savingPaths}
          conflictPaths={conflictPaths}
          timelineStatus={timelineStatus}
          onConflictsClick={handleOpenConflicts}
          isConflictsActive={conflictView !== null}
          conflictCount={conflicts.length}
          hiddenPaths={hiddenPaths}
          focusedFolder={focusedFolder}
          showAll={showAllFolders}
          onToggleShowAll={() => setShowAllFolders(v => !v)}
        />

        {/* Right panel */}
        <div className="v2-right">
          {/* Conflict view — overlays the right panel when active */}
          {conflictView && (
            <div className="v2-right-conflict-overlay">
              {conflictView === 'inbox' ? (
                <ConflictInbox
                  conflicts={conflicts}
                  loading={conflictsLoading}
                  onSelect={handleSelectConflict}
                  onBack={handleCloseConflicts}
                />
              ) : (
                <ConflictResolveView
                  conflict={resolvingConflict}
                  onBack={handleConflictBackToList}
                  onResolved={handleConflictResolved}
                  toast={toast}
                />
              )}
            </div>
          )}

          {/* Move mode hint banner */}
          {moveMode && (
            <div className="v2-move-hint">
              <span className="v2-move-hint-icon">&#x2190;</span>
              <span>Move block to node</span>
              <button className="v2-move-hint-cancel" onClick={(e) => { e.stopPropagation(); cancelMove(); }}>Cancel</button>
            </div>
          )}

          {/* Actions row */}
          <div className="actions">
            {selectedNode && !isTimelineView && (
              <span className="v2-current-node">
                {renderNodeIcon(nodeIcons[selectedNode.path] || nodeDefaults?.file_icon || '\u{1F4C4}')}
                <span className="v2-current-node-name">{selectedNode.path.split('/').pop()}</span>
                {syncState ? (
                  syncState === 'syncing' ? (
                    <span className="v2-sync-status" title="Syncing…">
                      <Save size={14} strokeWidth={2} />
                    </span>
                  ) : (
                    <span className="v2-sync-status" title="Saving…">
                      <Upload size={14} strokeWidth={2} />
                    </span>
                  )
                ) : lastUpdatedAt && (
                  <span className="v2-node-updated" title={`Latest sync ${formatUpdatedAt(lastUpdatedAt)}`}>
                    <RefreshCw size={14} strokeWidth={2} />
                    <span>{formatUpdatedAt(lastUpdatedAt)}</span>
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Block list or empty state */}
          {!selectedNode ? (
            <div className="v2-right-empty">
              <div style={{ fontWeight: 'var(--weight-semibold)' }}>Welcome to JulyNote</div>
            </div>
          ) : blockLoading ? (
            <div className="v2-right-empty">
              <span className="spinner" />
              <div>Loading...</div>
            </div>
          ) : nodeOffline ? (
            <div className="v2-right-empty">
              <div className="v2-right-empty-icon">&#x1F4F6;</div>
              <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-xs)' }}>
                Offline
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                Couldn't load this file. Reconnect and try again.
              </div>
            </div>
          ) : nodeNotFound ? (
            <div className="v2-right-empty">
              <div className="v2-right-empty-icon">&#x1F5D1;</div>
              <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-xs)' }}>
                File not found
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                This file may have been deleted from another place.
              </div>
              <div style={{ marginTop: 'var(--space-md)' }}>
                <button className="btn btn-primary" onClick={handleRemoveQuickAccess}>
                  Remove from Quick Access
                </button>
              </div>
            </div>
          ) : isTimelineView ? (
            <>
              <TimelineNode
                key={selectedNode.path}
                folderPath={resolveTimelineFolderPath(selectedNode)}
                isInbox={isInboxPath(selectedNode.path)}
                moveMode={moveMode}
                swipeEnabled={swipeEnabled}
                onDateChange={handleTimelineDateChange}
                onMoveBlock={(filePath, blockIndex, content) => {
                  setMoveSource({ filePath, blockIndex, blockContent: content });
                  setMoveMode(true);
                  setLeftWidth(PANEL_MAX);
                }}
                onSwipeProgress={handleSwipeProgress}
                onSwipeCommit={handleSwipeCommit}
                onSwipeCancel={() => { setDragCommit(false); setScreenSwipe(null); }}
                nodeList={flatNodeList}
                onSendToNode={handleSendToNode}
                destinations={blockModalDestinations}
                blockAutoSaveWord={blockAutoSaveWord}
                blockAutoSaveDelay={blockAutoSaveDelay}
              />

              {/* Move mask */}
              {moveMode && (
                <div className="v2-move-mask" onClick={cancelMove} />
              )}
            </>
          ) : (
            <>
              {inboxOffline && (
                <div className="v2-inbox-offline-banner">
                  <div>
                    <strong>Offline</strong>
                    <span>Inbox content couldn't load. You can still add blocks — they'll be merged when you're back online.</span>
                  </div>
                  <button className="btn btn-primary" onClick={handleInboxRetry}>
                    Retry
                  </button>
                </div>
              )}
              <NodeV2BlockList
                blocks={blocks}
                onEditBlock={conflictInfo ? handleOpenCurrentConflict : openEditBlock}
                onAddBlock={conflictInfo ? handleOpenCurrentConflict : openNewBlock}
                onMoveBlock={startMove}
                moveMode={moveMode}
                swipeEnabled={swipeEnabled}
                enableReorder
                onSwipeProgress={handleSwipeProgress}
                onSwipeCommit={handleSwipeCommit}
                onSwipeCancel={() => { setDragCommit(false); setScreenSwipe(null); }}
                onReorderEnd={handleReorderEnd}
                selectedFilePath={selectedNode?.path}
                refreshing={refreshing}
                onPullToRefresh={handlePullToRefresh}
                conflictIndices={conflictInfo ? computeUnsyncedIndices(blocks, conflictInfo.remoteContent) : null}
                unsyncedIndices={computeUnsyncedIndices(blocks, baseContent)}
                onOpenConflict={handleOpenCurrentConflict}
                />

              {/* Move mask */}
              {moveMode && (
                <div className="v2-move-mask" onClick={cancelMove} />
              )}
            </>
          )}
          {/* Panel-expanded mask — intercepts clicks to collapse the panel */}
          {leftWidth > PANEL_MIN && (
            <div className="v2-panel-mask" onClick={() => setLeftWidth(PANEL_MIN)} />
          )}
        </div>

        {/* Right sidebar swipe indicator — within app layout, overlays scrollbar */}
        <div className="v2-swipe-sidebar">
          <div
            className="v2-swipe-sidebar-fill"
            style={{
              height: screenSwipe
                ? `${Math.round((screenSwipe.ratio / COMMIT_RATIO) * 100)}%`
                : '0%',
              background: screenSwipe?.committed
                ? 'var(--accent)'
                : screenSwipe
                  ? 'rgba(128, 128, 128, 0.15)'
                  : 'transparent',
            }}
          />
        </div>
      </div>

      {/* Block delete confirmation popup */}
      <Modal
        show={!!confirmBlockDelete}
        title="Delete Block"
        onClose={() => setConfirmBlockDelete(null)}
      >
        <button
          className="btn btn-danger"
          style={{ width: '100%' }}
          onClick={handleConfirmBlockDelete}
        >
          Delete
        </button>
      </Modal>

      {/* Block edit — bottom panel on all platforms */}
      <BlockEditorPanel {...blockEditProps} />

      {/* Clear all blocks confirmation */}
      <Modal
        show={confirmClearOpen}
        title="Clear all blocks"
        onClose={() => setConfirmClearOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setConfirmClearOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleConfirmClearAllBlocks}
            >
              Clear all
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, color: 'var(--ink)' }}>
          Remove all {blocks.length} block{blocks.length !== 1 ? 's' : ''}?
        </p>
      </Modal>

      {/* Context menu (rendered at body level via fixed positioning) */}
      <NodeV2ContextMenu
        show={contextMenu.show}
        x={contextMenu.x}
        y={contextMenu.y}
        targetPath={contextMenu.targetPath}
        fromInbox={false}
        onMoveHere={() => executeMove(contextMenu.targetPath)}
        onCopyHere={() => executeCopy(contextMenu.targetPath)}
        onClose={closeContextMenu}
      />

      {/* Unified node/folder settings popup */}
      <NodeOptionsPanel
        show={!!nodeOptions}
        type={nodeOptions?.type || 'file'}
        nodePath={nodeOptions?.path || ''}
        nodeName={nodeOptions?.name || ''}
        currentIcon={nodeOptions ? (nodeIcons[nodeOptions.path] || '') : ''}
        isQuickAccess={nodeOptions ? (allNodeSettings[nodeOptions.path]?.quick_access === 'true') : false}
        isHidden={nodeOptions ? (allNodeSettings[nodeOptions.path]?.hidden === 'true') : false}
        onSetIcon={(icon) => { if (nodeOptions) setNodeSetting(nodeOptions.path, 'icon', icon); }}
        onClearIcon={() => { if (nodeOptions) setNodeSetting(nodeOptions.path, 'icon', ''); }}
        onToggleQuickAccess={() => {
          if (!nodeOptions) return;
          const current = allNodeSettings[nodeOptions.path]?.quick_access === 'true';
          setNodeSetting(nodeOptions.path, 'quick_access', current ? '' : 'true');
        }}
        onToggleHidden={() => {
          if (!nodeOptions) return;
          const current = allNodeSettings[nodeOptions.path]?.hidden === 'true';
          setNodeSetting(nodeOptions.path, 'hidden', current ? '' : 'true');
        }}
        onRename={handleRenameNode}
        onDelete={handleDeleteNode}
        onClose={handleCloseNodeOptions}
      />

      {/* Confirm delete popup */}
      <ConfirmPopup
        show={!!confirmDelete}
        title={confirmDelete?.type === 'folder' ? 'Delete Folder' : 'Delete Node'}
        message={confirmDelete?.type === 'folder'
          ? `Are you sure you want to delete the folder "${confirmDelete?.name || ''}" and all of its contents? This action cannot be undone.`
          : `Are you sure you want to delete "${confirmDelete?.name || ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* User Settings popup */}
      <UserSettingsModal
        show={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        openDataSourceSetup={openDataSourceSetup}
        onOpenHelp={handleOpenHelp}
        onOpenPrivacyPolicy={() => setPrivacyOpen(true)}
        onOpenMagicWordSettings={() => setQuickTextSettingsOpen(true)}
        onOpenFeedback={() => setFeedbackOpen(true)}
      />

      {/* Feedback popup */}
      <FeedbackPanel
        show={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />

      {/* Magic Word Settings popup */}
      <MagicWordSettingsModal
        show={quickTextSettingsOpen}
        onClose={() => setQuickTextSettingsOpen(false)}
        blockAutoSaveWord={blockAutoSaveWord}
        blockAutoSaveDelay={blockAutoSaveDelay}
        onSaveBlockSettings={handleSaveBlockSettings}
      />

      {/* Help popup */}
      <HelpDocPopup
        show={helpOpen}
        docName="help"
        onClose={() => setHelpOpen(false)}
      />

      {/* Privacy policy popup */}
      <HelpDocPopup
        show={privacyOpen}
        docName="privacy"
        title="Privacy Policy"
        onClose={() => setPrivacyOpen(false)}
      />

      {/* Name prompt — first-run, skippable */}
      <NamePromptModal
        show={namePrompt.show}
        defaultName={namePrompt.defaultName}
        sourceHint={namePrompt.sourceHint}
        onClose={() => setNamePrompt(p => ({ ...p, show: false }))}
      />

      {/* New node filename prompt */}
      {filenamePrompt.show && (
        <div className="modal-overlay" onClick={() => setFilenamePrompt({ show: false, targetPath: '' })}>
          <div className="modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New node file</h3>
              <button className="modal-close" onClick={() => setFilenamePrompt({ show: false, targetPath: '' })}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <label style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)' }}>
                  Enter filename for the new node:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, fontSize: 'var(--text-sm)' }}>
                  {filenamePrompt.targetPath && (
                    <span style={{ color: 'var(--ink-muted)' }}>{filenamePrompt.targetPath}/</span>
                  )}
                  <input
                    style={{
                      flex: 1,
                      padding: 'var(--space-sm) var(--space-md)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '16px',
                      border: 'var(--border-thin)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--paper)',
                      color: 'var(--ink)',
                      outline: 'none',
                    }}
                    value={newFilename}
                    onChange={e => setNewFilename(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') confirmNewNode(); }}
                    autoFocus
                    onFocus={e => e.target.select()}
                  />
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                  <button className="btn" onClick={() => setFilenamePrompt({ show: false, targetPath: '' })}>Cancel</button>
                  <button className="btn btn-primary" onClick={confirmNewNode} disabled={!newFilename.trim()}>
                    Create &amp; Move
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add node popup */}
      {addNodePopup.show && (
        <div className="modal-overlay" onClick={() => setAddNodePopup({ show: false, targetPath: '' })}>
          <div className="modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Node</h3>
              <button className="modal-close" onClick={() => setAddNodePopup({ show: false, targetPath: '' })}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {/* Path (read-only) */}
                <div>
                  <div className="v2-section-label">Path</div>
                  <div style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--ink-muted)',
                    fontFamily: 'var(--font-mono)',
                    wordBreak: 'break-all',
                  }}>
                    {(() => {
                      const n = addNodeName.trim();
                      const isFolder = addNodeType === 'folder';
                      const full = !n ? '' : (isFolder ? n : (n.endsWith('.md') ? n : n + '.md'));
                      return full
                        ? (addNodePopup.targetPath ? `${addNodePopup.targetPath}/${full}` : full)
                        : addNodePopup.targetPath;
                    })()}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'var(--rule)', margin: 0 }} />

                {/* Type */}
                <div>
                  <div className="v2-section-label">Type</div>
                  <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                    <button
                      className={`btn${addNodeType === 'file' ? ' btn-primary' : ''}`}
                      onClick={() => setAddNodeType('file')}
                      style={{ flex: 1 }}
                    >
                      File
                    </button>
                    <button
                      className={`btn${addNodeType === 'folder' ? ' btn-primary' : ''}`}
                      onClick={() => setAddNodeType('folder')}
                      style={{ flex: 1 }}
                    >
                      Folder
                    </button>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <div className="v2-section-label">Name</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, fontSize: 'var(--text-sm)' }}>
                    <input
                      style={{
                        flex: 1,
                        padding: 'var(--space-sm) var(--space-md)',
                        fontFamily: 'var(--font-body)',
                        fontSize: '16px',
                        border: 'var(--border-thin)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--paper)',
                        color: 'var(--ink)',
                        outline: 'none',
                      }}
                      value={addNodeName}
                      onChange={e => { setAddNodeName(e.target.value); if (addNodeError) setAddNodeError(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleConfirmAddNode(); }}
                      autoFocus
                      placeholder={addNodeType === 'folder' ? 'e.g. projects' : 'e.g. my-note'}
                    />
                    {addNodeType !== 'folder' && (
                      <span style={{ color: 'var(--ink-muted)', flexShrink: 0, marginLeft: 'var(--space-xs)', fontSize: 'var(--text-xs)' }}>.md</span>
                    )}
                  </div>
                  {addNodeError && (
                    <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-xs)' }}>
                      {addNodeError}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                  <button className="btn" onClick={() => setAddNodePopup({ show: false, targetPath: '' })} disabled={addNodeSubmitting}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleConfirmAddNode} disabled={!addNodeName.trim() || addNodeSubmitting}>
                    {addNodeSubmitting ? (
                      <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Node;
